import { ListViewColumnDefinition } from '@components/UI-Components/StandardTableView/models/ListviewColumnDefinition'
import { WholesaleGridCard } from '@components/UI-Components/WholesaleGridCard'
import { config } from '@config'
import Divider from '@material-ui/core/Divider'
import { ProductContainerRow } from '@modules/collection/components/ProductContainerRow'
import { CardSkeleton } from '@modules/common/component/CardSkeleton'
import { ListRowSkeleton } from '@modules/common/component/ListRowSkeleton'
import { useBuyingSessionGroup } from '@modules/wholesale/buyingGroupSummary/graphql/hooks'
import { useBuyingSessionProduct } from '@modules/wholesale/buyingSessionGroupProductWrapper/graphql/hooks'
import { productForecastUIStore } from '@modules/wholesale/buyingSessionGroupProductWrapper/stores/productForecastUIStore'
import { getEmptyArrayOfLength } from '@services/commonServices'
import { wholeSaleProductService } from '@services/wholesale/productService'
import { isZAMUserInPrebuyingSession } from '@services/wholesale/wholesaleBuyingSessionService'
import { wholesalePriceService } from '@services/wholesale/wholesalePriceService'
import { stores, strings } from '@stores'
import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import { useObserver } from 'mobx-react-lite'
import React, { useCallback, useMemo } from 'react'
import { StandardTreeViewStore } from '@components/UI-Components/StandardTreeView/Store/TreeViewStore'
import { WHOLESALEPRODUCTROWHEIGHT } from '@constants'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import ClusterStatusDropdown from '@modules/wholesale/assortmentProductWrapper/components/ClusterStatusDropDown'
import { useQueryGetClusterStatus } from '@modules/wholesale/clusterSetting/graphql/getClusterStatus'
import { ViewTabs } from '@modules/common/models/enums'
import { isRetailChannel } from '@services/wholesale/wholesaleBuyingSessionGroupsServices'
import isString from 'lodash/isString'
import { counterOfferUIStore } from '../stores/CounterOffer/CounterOfferUIStore'
import * as css from './CollectionProductRowWrapper.css'
import { useTranslation } from 'react-i18next'
import { DefaultConfig } from '@constants'

export const zoneRowHeight = 25.6

interface ICollectionProductRowWrapper {
  style?: any
  node?: any
  stickyStyle?: any
  listHeaders?: Array<ListViewColumnDefinition>
  canTableFitInView?: boolean
  productEnumList?: any
  retailZones?: any
  enumLoading?: boolean
  retailZonesLoading?: boolean
}

export const CollectionProductRowWrapper: React.FunctionComponent<
  ICollectionProductRowWrapper
> = ({
  node,
  style,
  stickyStyle,
  listHeaders,
  canTableFitInView,
  productEnumList,
  retailZones,
  enumLoading,
  retailZonesLoading
}) => {
  return useObserver(function useHook() {
    const { t } = useTranslation()
    /**
     * Read activity, childrenMeta (hierarhcy levels: family -> line / family / line) and chilrenMetaValues (name of family / line) from node
     */
    const { category: activity, childrenMeta, childrenMetaValues, categoryIds } = node
    const {
      wsBuyingSessionProduct: {
        whereInputForBSPForGroup,
        whereInputForBSPForBS,
        handleTagDrop,
        handleListViewTagDrop,
        favoriteClickHandler,
        mouseDownHandlerForBSProducts,
        rightClickHandlerForBulkOp,
        filterProductsBasedOnFilterAndSearch,
        sortProductsBasedOnSortType,
        retailBuyProducts,
        retailBuyComments,
        retailBuyProductsLength,
        productSelectionStore: { isProductSelected }
      },
      nav: {
        buyingSession,
        queryParams: { productLayout, tab, buyingSessionGroupId, zoneId },
        onProductDetailClick
      },
      search: { searchKey },
      filterModalStore: { wsAppliedFilters },
      attributeSortStore: { selectedSortType },
      buyingSessionProductPriceStore: { getPriceByBuyingSessionAndClient, fetchPriceData }
    } = stores
    const { init, productForecastData, upsertForecastData, targetCurrency } =
      productForecastUIStore
    const { selectedNavItems } = StandardTreeViewStore
    const { rmClustersData } = counterOfferUIStore

    // create mock array
    const mockProducts = getEmptyArrayOfLength(node.products)

    // Build where input for buying session product and order product queries
    const whereQueryObject = buyingSession
      ? whereInputForBSPForBS
      : whereInputForBSPForGroup

    const queryVariableObject = whereQueryObject(
      activity,
      childrenMeta,
      childrenMetaValues
    )
    const { data: buyingSessionGroupData } = useBuyingSessionGroup(buyingSessionGroupId)
    const { data, loading } = useBuyingSessionProduct(
      queryVariableObject,
      'cache-and-network'
    )

    React.useEffect(() => {
      if (productLayout === 'list') {
        if (!selectedNavItems) return

        const initCategoryValues = selectedNavItems?.some(navItem => {
          if (!isString(navItem)) return false
          const items = navItem?.split(':')
          return (
            items?.[0] === activity &&
            childrenMetaValues?.some(
              child => items?.[1] === child || items?.[2] === child
            )
          )
        })
        init(
          {
            buyingSessionGroupId,
            buyingSessionId: buyingSession,
            categoryValues: [activity, ...(childrenMetaValues || [])]
          },
          initCategoryValues,
          true
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      buyingSessionGroupId,
      buyingSession,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(categoryIds),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(selectedNavItems),
      productLayout
    ])

    const isGridView = productLayout === strings.grid
    const products = filterProductsBasedOnFilterAndSearch(
      data?.findVIPBuyingSessionProduct?.Items ?? [],
      searchKey,
      wsAppliedFilters
    )

    const sortedProducts =
      retailBuyProductsLength && products.length && selectedSortType
        ? sortProductsBasedOnSortType(
            products,
            selectedSortType,
            retailBuyProducts,
            zoneId
          )
        : products
    const productEnums = productEnumList?.findEnumList?.Items ?? []

    /**
     * Call hook for fetching price of products
     */
    const variableForFetchingPrices = useMemo(
      () => wholesalePriceService.getVariableForFetchingPrices(null, null),
      []
    )

    React.useEffect(() => {
      if (buyingSessionGroupId) {
        fetchPriceData(
          variableForFetchingPrices,
          buyingSessionGroupId,
          buyingSessionGroupId
        )
      }
    }, [buyingSessionGroupId, fetchPriceData, variableForFetchingPrices])

    const { items: retailClusterStatuses = [] } = useQueryGetClusterStatus(
      isRetailChannel(buyingSessionGroupData?.buyingSessionGroup?.channels)
        ? VIPChannel.Retail
        : null
    )

    // calculate row height for retail tab zones
    const getCustomRowHeight = useCallback(
      product => {
        if (
          tab === ViewTabs.RTL_BUY.toLowerCase() ||
          tab === ViewTabs.FORECAST.toLocaleLowerCase()
        ) {
          const rowHeight = product?.zoneWiseData?.length * zoneRowHeight
          return rowHeight > WHOLESALEPRODUCTROWHEIGHT ? rowHeight : null
        }
        return null
      },
      [tab]
    )
    return (
      <div style={style} className={classNames({ [css.gridRoot]: isGridView })}>
        {isGridView ? (
          (loading || enumLoading || retailZonesLoading) && isEmpty(data) ? (
            map(mockProducts, (product, index) => (
              <CardSkeleton key={'cardSkeleton' + index} />
            ))
          ) : (
            map(
              wholeSaleProductService.processProductsForGridView(
                products,
                productEnums,
                getPriceByBuyingSessionAndClient(buyingSessionGroupId),
                null,
                t,
                buyingSessionGroupData?.buyingSessionGroup?.channels
              ),
              product => {
                const isSelectedForBulkOp = isProductSelected(product?.productId)
                return (
                  <WholesaleGridCard
                    product={product}
                    key={product.productId}
                    handleTagDrop={handleTagDrop}
                    isSelectedForBulkOp={isSelectedForBulkOp}
                    mouseDownHandler={mouseDownHandlerForBSProducts(product)}
                    rightClickHandler={rightClickHandlerForBulkOp}
                    favoriteClickHandler={favoriteClickHandler(product.productId)}
                    clickHandler={onProductDetailClick(product.node.Id)}
                    isZAMUserInPrebuyingSession={isZAMUserInPrebuyingSession(
                      product.buyingSession
                    )}
                  />
                )
              }
            )
          )
        ) : (loading || enumLoading) && isEmpty(data) ? (
          map(mockProducts, (product, index) => (
            <React.Fragment key={'ListRowSkeleton' + index}>
              <ListRowSkeleton />
              <Divider />
            </React.Fragment>
          ))
        ) : (
          <ProductContainerRow
            isWholesale={true}
            headers={listHeaders}
            handleDrop={product => {
              return handleListViewTagDrop(product, t)
            }}
            stickyStyle={stickyStyle}
            canTableFitInView={canTableFitInView}
            products={wholeSaleProductService.processProductsForListViewData(
              retailBuyProductsLength && selectedSortType
                ? (sortedProducts as any[])
                : products,
              retailBuyProducts,
              retailBuyComments,
              rmClustersData,
              productEnums,
              tab as any,
              getPriceByBuyingSessionAndClient(buyingSessionGroupId),
              '',
              t,
              {
                targetCurrency: config.globalCurrency || DefaultConfig.GLOBAL_CURRENCY
              } as any,
              { productForecastData, upsertForecastData, targetCurrency }
            )}
            getCustomRowHeight={getCustomRowHeight}
          />
        )}
        <ClusterStatusDropdown clusterStatuses={retailClusterStatuses} />
      </div>
    )
  })
}
